import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertTriangle,
  ArrowRight,
  Copy,
  KeyRound,
  Mail,
  Search,
  ShieldCheck,
  UserCircle,
  UserMinus,
  UserPlus,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { KpiCard } from "@/components/ui/kpi-card";
import {
  TableShell,
  TableHeadRow,
  TableRow,
  TableHeadCell,
  TableCell,
} from "@/components/ui/table-shell";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useOpusUsers } from "@/stores/opus-users";
import {
  OpusUserCreateSchema,
  type OpusUserCreate,
  type OpusRole,
} from "@/api/schemas/opus-users";
import { ApiError } from "@/api/client";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

const COLS = "1.6fr 1.3fr 1fr 0.7fr 1.1fr 1fr";

/** Two-letter ISO 3166-1 country code → flag emoji. */
function flag(country: string | null | undefined): string {
  if (!country || country.length !== 2) return "";
  const A = "A".charCodeAt(0);
  return [...country.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + (c.charCodeAt(0) - A)))
    .join("");
}

function roleBadge(role: OpusRole) {
  switch (role) {
    case "admin":
      return (
        <Badge variant="info" size="sm">
          admin
        </Badge>
      );
    case "developer":
      return (
        <Badge variant="info" size="sm">
          developer
        </Badge>
      );
    case "viewer":
      return (
        <Badge variant="neutral" size="sm">
          viewer
        </Badge>
      );
  }
}

function timeAgo(iso?: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

/* ---------- sub-components ---------- */

function CreateUserCard() {
  const { create } = useOpusUsers();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<OpusUserCreate>({
    resolver: zodResolver(OpusUserCreateSchema),
    defaultValues: {
      username: "",
      email: "",
      fullName: "",
      role: "developer",
      title: "",
    },
  });

  async function onCreate(values: OpusUserCreate) {
    try {
      await create(values);
      toast.success(`Created ${values.username}`);
      reset({
        username: "",
        email: "",
        fullName: "",
        role: "developer",
        title: "",
      });
    } catch (err) {
      toast.error("Unable to create user", {
        description:
          err instanceof ApiError
            ? `API error ${err.status}`
            : err instanceof Error
              ? err.message
              : "unknown",
      });
    }
  }

  return (
    <Card>
      <CardContent>
        <form
          noValidate
          onSubmit={(e) => void handleSubmit(onCreate)(e)}
          className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_1.3fr_1.3fr_0.6fr_1fr_auto] lg:items-end"
        >
          <div className="lg:col-span-6">
            <CardTitle>Invite a new operator</CardTitle>
            <CardSub>
              The user receives an email with a one-time sign-in link valid
              for 24h.
            </CardSub>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ou-username">Username</Label>
            <Input
              id="ou-username"
              placeholder="e.g. esantizo"
              invalid={!!errors.username}
              {...register("username")}
            />
            {errors.username ? (
              <span className="text-xs text-rose">
                {errors.username.message}
              </span>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ou-email">Email</Label>
            <div className="relative">
              <Mail
                size={14}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
              />
              <Input
                id="ou-email"
                type="email"
                autoComplete="off"
                placeholder="user@guest-tek.com"
                className="pl-9"
                invalid={!!errors.email}
                {...register("email")}
              />
            </div>
            {errors.email ? (
              <span className="text-xs text-rose">
                {errors.email.message}
              </span>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ou-name">Full name</Label>
            <Input
              id="ou-name"
              placeholder="First Last"
              invalid={!!errors.fullName}
              {...register("fullName")}
            />
            {errors.fullName ? (
              <span className="text-xs text-rose">
                {errors.fullName.message}
              </span>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ou-role">Role</Label>
            <select
              id="ou-role"
              {...register("role")}
              className="h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
            >
              {/* Three real tiers — admin (everything), developer
                  (reads + propose-then-confirm), viewer (read-only). */}
              <option value="admin">admin</option>
              <option value="developer">developer</option>
              <option value="viewer">viewer</option>
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ou-title">Title (optional)</Label>
            <Input
              id="ou-title"
              placeholder="e.g. Senior Network Engineer"
              {...register("title")}
            />
          </div>
          <Button type="submit" disabled={isSubmitting}>
            <UserPlus size={14} />
            {isSubmitting ? "Inviting…" : "Invite"}
            <ArrowRight size={13} />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function UsersTableCard() {
  const { data, status, setActive, setAgentEnabled, resetPassword } =
    useOpusUsers();
  const [resetResult, setResetResult] = useState<{
    username: string;
    tempPassword: string;
  } | null>(null);
  const [resetTargetId, setResetTargetId] = useState<number | null>(null);
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.fullName.toLowerCase().includes(q) ||
        u.role.toLowerCase().includes(q) ||
        (u.title ?? "").toLowerCase().includes(q),
    );
  }, [data, query]);

  async function handleResetPassword(
    userId: number,
    username: string,
  ): Promise<void> {
    const ok = window.confirm(
      `Reset password for ${username}? They will be signed out everywhere and need the new temp password to log in again.`,
    );
    if (!ok) return;
    setResetTargetId(userId);
    try {
      const tempPassword = await resetPassword(userId);
      setResetResult({ username, tempPassword });
    } catch (err) {
      const errStatus = err instanceof ApiError ? err.status : null;
      toast.error(
        errStatus === 403
          ? "Only admins can reset passwords"
          : errStatus === 404
            ? "User not found (was it just deleted?)"
            : "Reset failed — check logs",
      );
    } finally {
      setResetTargetId(null);
    }
  }

  function copyTempPassword(): void {
    if (!resetResult) return;
    void navigator.clipboard.writeText(resetResult.tempPassword).then(
      () => toast.success("Temp password copied"),
      () => toast.error("Couldn't copy — select and copy manually"),
    );
  }

  return (
    <>
      <Dialog
        open={!!resetResult}
        onOpenChange={(next) => {
          if (!next) setResetResult(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogTitle>Password reset for {resetResult?.username}</DialogTitle>
          <DialogDescription>
            Copy the temp password and send it via a secure channel. The user
            must change it from <strong>Profile → Change password</strong> after
            signing in. This value is not stored on the server — once you close
            this dialog it is gone.
          </DialogDescription>
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2 font-mono text-sm">
            <span className="flex-1 select-all break-all">
              {resetResult?.tempPassword}
            </span>
            <button
              type="button"
              onClick={copyTempPassword}
              aria-label="Copy password"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-line bg-surface text-fg-dim hover:bg-aurora-soft hover:text-violet"
            >
              <Copy size={13} />
            </button>
          </div>
          <div className="mt-5 flex justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setResetResult(null)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <div>
            <CardTitle>Operators</CardTitle>
            <CardSub className="mt-1">
              {visible.length}/{data.length} shown
            </CardSub>
          </div>
          <div className="relative max-w-xs flex-1">
            <Search
              size={14}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name, role, title…"
              className="pl-9"
            />
          </div>
        </div>

        {status === "loading" && data.length === 0 ? (
          <TableSkeleton
            className="rounded-none border-0 border-t border-line"
            columns={COLS}
            columnCount={6}
            rows={6}
          />
        ) : (
          <TableShell className="rounded-none border-0 border-t border-line">
            <TableHeadRow columns={COLS}>
              <TableHeadCell>User</TableHeadCell>
              <TableHeadCell>Email · username</TableHeadCell>
              <TableHeadCell>Title</TableHeadCell>
              <TableHeadCell>Role</TableHeadCell>
              <TableHeadCell>Last login</TableHeadCell>
              <TableHeadCell />
            </TableHeadRow>
            {visible.map((u) => (
              <TableRow
                key={u.id}
                columns={COLS}
                className={u.isActive ? "" : "opacity-60"}
              >
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-aurora-grad text-bg-0">
                      <UserCircle size={14} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-semibold text-fg">
                          {u.fullName}
                        </span>
                        <span title={u.country ?? undefined}>
                          {flag(u.country)}
                        </span>
                      </div>
                      {!u.isActive ? (
                        <div className="text-[10.5px] text-fg-mute">
                          deactivated
                        </div>
                      ) : null}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-mono text-[11px] text-fg-dim">
                    {u.email}
                  </div>
                  <div className="font-mono text-[10.5px] text-fg-mute">
                    @{u.username}
                  </div>
                </TableCell>
                <TableCell className="text-xs text-fg-dim">
                  {u.title ?? "—"}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-1">
                    {roleBadge(u.role)}
                    {u.isStaff ? (
                      <Badge variant="neutral" size="sm">
                        staff
                      </Badge>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="text-xs text-fg-dim">
                  {timeAgo(u.lastLoginAt)}
                </TableCell>
                <TableCell className="flex justify-end gap-1.5">
                  <button
                    type="button"
                    onClick={() => void setAgentEnabled(u.id, !u.agentEnabled)}
                    title={
                      u.agentEnabled
                        ? "Disable AI agent for this user (they keep all other access)"
                        : "Re-enable AI agent for this user"
                    }
                    className={
                      "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors " +
                      (u.agentEnabled
                        ? "border-violet/40 bg-aurora-soft text-violet hover:brightness-110"
                        : "border-line bg-surface text-fg-mute hover:bg-surface-2")
                    }
                  >
                    {u.agentEnabled ? "AI: on" : "AI: off"}
                  </button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void handleResetPassword(u.id, u.username)}
                    disabled={resetTargetId === u.id}
                    title="Generate a new temp password for this user"
                  >
                    <KeyRound size={13} />
                    {resetTargetId === u.id ? "Resetting…" : "Reset pw"}
                  </Button>
                  <Button
                    size="sm"
                    variant={u.isActive ? "ghost" : "primary"}
                    onClick={() => void setActive(u.id, !u.isActive)}
                  >
                    {u.isActive ? (
                      <>
                        <UserMinus size={13} />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <UserPlus size={13} />
                        Re-enable
                      </>
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-fg-mute">
                {query ? `No users match "${query}".` : "No users yet."}
              </div>
            ) : null}
          </TableShell>
        )}
      </Card>
    </>
  );
}

export function UsersPage() {
  const animatedRootRef = useUiAutoAnimate();
  const { data, error, load } = useOpusUsers();

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(() => {
    return {
      total: data.length,
      active: data.filter((u) => u.isActive).length,
      admins: data.filter((u) => u.role === "admin" && u.isActive).length,
      developers: data.filter((u) => u.role === "developer" && u.isActive)
        .length,
      viewers: data.filter((u) => u.role === "viewer" && u.isActive).length,
    };
  }, [data]);

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Users &amp; Tenants</span>
      </div>

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Users &amp; Tenants
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Opus operators — the people who can sign in to this app. Distinct
            from per-vendor users (Aruba Central accounts live under{" "}
            <Link
              to="/aruba/users"
              className="font-semibold text-violet hover:opacity-80"
            >
              /aruba/users
            </Link>
            ).
          </p>
        </div>
        <Badge variant="info" size="md">
          <ShieldCheck size={11} />
          {counts.active}/{counts.total} active
        </Badge>
      </div>

      {error ? (
        <output className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach">
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      <div className="grid grid-cols-4 gap-3.5">
        <KpiCard
          label="Operators"
          value={counts.active}
          icon={<UserCircle size={14} />}
          trend="flat"
          trendLabel={`${counts.total - counts.active} disabled`}
        />
        <KpiCard
          label="Admins"
          value={counts.admins}
          trend="flat"
          trendLabel="full read/write incl. high-impact tools"
        />
        <KpiCard
          label="Developers"
          value={counts.developers}
          trend="flat"
          trendLabel="reads + propose-then-confirm"
        />
        <KpiCard
          label="Viewers"
          value={counts.viewers}
          trend="flat"
          trendLabel="read-only across the estate"
        />
      </div>

      <CreateUserCard />
      <UsersTableCard />
    </div>
  );
}
